import { normalizeDiagnosticNotifications } from 'api/RobotApi'
import { DiagnosticNotificationExportDto } from 'api/asyncapi'
import { Header } from 'components/Header/Header'
import { useEffect, useRef, useState } from 'react'
import { Outlet, useLocation } from 'react-router-dom'
import DiagnosticOverlay from 'components/Diagnostic/DiagnosticOverlay'
import useDiagnosticStore from 'store/diagnostic'
import { resolveDiagnosticNotification, GLOBAL_WS_PATH } from 'utils/diagnostic'
import WebsocketManager, { SOCKET_MESSAGES } from 'utils/WebsocketManager'

import style from './Layout.module.css'

/** Максимальное время ожидания ответа сервера на резолв уведомления, мс. */
const RESOLVE_TIMEOUT = 10000

function MainPageLayout() {
  const { notifications, replaceRobotNotifications, clearNotifications, removeNotification } = useDiagnosticStore()
  const location = useLocation()

  // На табе диагностики (/asset/:assetId/diagnostic) уведомления робота уже
  // отображаются в секции «Активные уведомления», поэтому скрываем их из общего
  // оверлея, чтобы не дублировать. На остальных табах робота уведомления этого
  // робота показываем.
  const robotIdMatch = location.pathname.match(/^\/asset\/(\d+)\/diagnostic/)
  const currentRobotId = robotIdMatch ? Number(robotIdMatch[1]) : undefined
  const visibleNotifications = currentRobotId ? notifications.filter(n => n.robot_id !== currentRobotId) : notifications

  // Обработка нажатой кнопки оверлея: пока идёт резолв, кнопка показывает загрузку;
  // уведомление снимается после подтверждения сервера (DIAGNOSTIC_RESPONSE) или таймаута.
  const [pendingAction, setPendingAction] = useState<{ notifId: string; buttonId: string } | null>(null)
  const pendingActionRef = useRef(pendingAction)
  pendingActionRef.current = pendingAction

  useEffect(() => {
    WebsocketManager.init(GLOBAL_WS_PATH)

    const handleDiagnosticNotification = (event: DiagnosticNotificationExportDto) => {
      const normalized = normalizeDiagnosticNotifications(event.data)
      if (normalized.length === 0) {
        // Пустой список на глобальном канале = нет активных уведомлений.
        clearNotifications()
        return
      }
      replaceRobotNotifications(normalized)
    }

    WebsocketManager.addEventListener(SOCKET_MESSAGES.DIAGNOSTIC_NOTIFICATION, handleDiagnosticNotification, GLOBAL_WS_PATH)

    return () => {
      WebsocketManager.removeEventListener(
        SOCKET_MESSAGES.DIAGNOSTIC_NOTIFICATION,
        handleDiagnosticNotification,
        GLOBAL_WS_PATH
      )
      WebsocketManager.disconnect(GLOBAL_WS_PATH)
    }
  }, [replaceRobotNotifications, clearNotifications])

  // Подписка на подтверждение резолва от сервера.
  useEffect(() => {
    const handleResponse = () => {
      const pending = pendingActionRef.current
      if (!pending) return
      removeNotification(pending.notifId)
      setPendingAction(null)
    }

    WebsocketManager.addEventListener(SOCKET_MESSAGES.DIAGNOSTIC_RESPONSE, handleResponse, GLOBAL_WS_PATH)
    return () => {
      WebsocketManager.removeEventListener(SOCKET_MESSAGES.DIAGNOSTIC_RESPONSE, handleResponse, GLOBAL_WS_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removeNotification])

  // Фолбэк: если сервер не подтвердил вовремя, снимаем загрузку, чтобы кнопка не зависла.
  useEffect(() => {
    if (!pendingAction) return
    const timer = setTimeout(() => setPendingAction(null), RESOLVE_TIMEOUT)
    return () => clearTimeout(timer)
  }, [pendingAction])

  const handleAction = async (notificationId: string, buttonId: string) => {
    if (pendingAction) return
    const notification = notifications.find(n => n.notification_id === notificationId)
    if (!notification) return

    setPendingAction({ notifId: notificationId, buttonId })
    try {
      resolveDiagnosticNotification(notification, buttonId)
    } catch (error) {
      console.error('Failed to send diagnostic response:', error)
      setPendingAction(null)
    }
  }

  return (
    <>
      <div className={style.header}>
        <Header.Main />
      </div>
      <div className={style.container}>
        <Outlet />
      </div>
      <DiagnosticOverlay
        notifications={visibleNotifications}
        onAction={handleAction}
        loadingNotifId={pendingAction?.notifId}
        loadingButtonId={pendingAction?.buttonId}
      />
    </>
  )
}

function PublicPageLayout() {
  return (
    <>
      <div className={style.header}>
        <Header.Public />
      </div>
      <div className={style.container}>
        <Outlet />
      </div>
    </>
  )
}

export const Layout = {
  MainPage: MainPageLayout,
  PublicPage: PublicPageLayout,
}
